import { useState } from 'react';

import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";

import { Step3 as Step3Airtable } from './Airtable/Step3/index';
import { Step4 as Step4Airtable } from './Airtable/Step4/index';
import { Step3 as Step3Salesforce } from './Salesforce/Step3/index';
import { Step4 as Step4Salesforce } from './Salesforce/Step4';
import { Step1, type Project } from './Step1';
import { Step2 } from './Step2';


import { crmSystem } from '@/constants/crm/_shared/systems';
import { AirtableBase } from "@/types/crm/airtable/common";
import { CrmComponentResolver } from './CrmComponentResolver';

import { type ParameterFormSchemaType as Step3SalesforceSchemaType } from '../../../../parameters/components/organisms/ParameterForm/ParameterForm.types';

type Props = {
    label?: string | React.ReactNode;
    buttonClassName?: string;
    buttonVariant?: "default" | "link" | "accent" | "destructive" | "outline" | "secondary" | "ghost" | "info" | "muted" | "indigo" | "pink" | "cyan" | "success" | null;
    projects?: Project[];
    defaultStep?: number;
}

const AddCrmModal = ({
    label="New Configuration (with Projects)",
    buttonClassName="",
    buttonVariant="default",
    projects=[],
    defaultStep = 1
} : Props) => {
    const [showDialog, setShowDialog] = useState(false);
    const [step, setStep] = useState(defaultStep);

    const defaultValues = {
        1: {data: [] as Project[]}, 
        2: {data: {name: "", system: ""}},
        3: {data: {} as {apiKey: string, bases: AirtableBase[]} | Step3SalesforceSchemaType},
    };

    const [data, setData] = useState({...defaultValues, 1: {data: projects}});

    const onNextHandler = ({currentStep, nextStep, data}: {currentStep: number, nextStep: number, data?: any}) => {
        if(data) {
            setData(prevData => ({
                ...prevData,
                [currentStep]: {data}
            }));
        }
        
        setStep(nextStep);
    };

    const onBackHandler = (previousStep: number) => {
        setStep(previousStep);
    };

    const onOpenHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setShowDialog(true);
    }
    const onCloseHandler = () => {
        setShowDialog(false);
    
        setData(defaultValues);
        setStep(1);
    }
    
    const tabNextStepHandler = (value: string) => {
        if((value === "2") && (data[1].data.length === 0)) {
            return;
        }
        if((value === "3") && (data[2].data.system.length === 0) && (data[2].data.name.length === 0)) {
            return;
        }
        if((value === "4")) {
            if(!data[2].data.system){
                return;
            }
            if((data[2].data.system === "salesforce") && (data[3].data?.parameter.project?.length === 0)) {
                return;
            }
            if((data[2].data.system === "collaborate") && (data[3].data?.parameter.project?.length === 0)) {
                return;
            }
            if((data[2].data.system === "airtable") && (data[3].data?.apiKey?.length === 0) && (data[3].data?.baseId?.length === 0)) {
                return;
            }
        }
        setStep(parseInt(value));
    }

    return (
        <>
            <Button size="sm" variant={buttonVariant} className={buttonClassName} onClick={onOpenHandler}>{label}</Button>

            <Dialog open={showDialog} onOpenChange={onCloseHandler} defaultOpen={false}>
                <DialogContent className="max-w-7xl">
                    <DialogHeader>
                        <DialogTitle className='m-0 font-bold text-start'>
                            Create CRM configuration
                        </DialogTitle>
                        <DialogDescription>
                            Structured setup workflow that facilitates the creation and configuration of projects
                        </DialogDescription>
                    </DialogHeader>

                    <ScrollArea className="max-h-[60vh] sm:max-h-[70vh] pe-3">
                        <div className="flex flex-col gap-4 px-1 mb-3">

                            <Tabs defaultValue="1" value={String(step)} onValueChange={tabNextStepHandler}>
                                <TabsList className={`flex h-auto`}>
                                    <TabsTrigger className="flex flex-col grow" value="1">
                                        <h2 className="">Step 1</h2>
                                        <p className="text-xs font-normal">Select Projects</p>
                                    </TabsTrigger>
                                    <TabsTrigger className="flex flex-col grow" value="2">
                                        <h2 className="">Step 2</h2>
                                        <p className="text-xs font-normal">Select CRM Type</p>
                                    </TabsTrigger>
                                    <TabsTrigger className="flex flex-col grow" value="3">
                                        <h2 className="">Step 3</h2>
                                        <CrmComponentResolver 
                                            selectedCrmSystem={data[2].data.system as crmSystem}
                                            components={{
                                                'airtable': (
                                                    <p className="text-xs font-normal">Fetch Data</p>
                                                ),
                                                'salesforce': (
                                                    <p className="text-xs font-normal">Define CRM Elements</p>
                                                ),
                                                'collaborate': (
                                                    <p className="text-xs font-normal">Define CRM Elements</p>
                                                ),
                                            }}
                                        />
                                    </TabsTrigger>
                                    <TabsTrigger className="flex flex-col grow" value="4">
                                        <h2 className="">Step 4</h2>
                                        <CrmComponentResolver 
                                            selectedCrmSystem={data[2].data.system as crmSystem}
                                            components={{
                                                'airtable': (
                                                    <p className="text-xs font-normal">Connect Bases</p>
                                                ),
                                                'salesforce': (
                                                    <p className="text-xs font-normal">Fill Data Elements</p>
                                                ),
                                                'collaborate': (
                                                    <p className="text-xs font-normal">Fill Data Elements</p>
                                                ),
                                            }}
                                        />
                                    </TabsTrigger>
                                </TabsList>

                                <TabsContent value="1">
                                    <div className="flex flex-col gap-3">
                                        <Step1 
                                            defaultData={data[1].data}
                                            onNext={(data) => onNextHandler({currentStep:1, nextStep: 2, data})}
                                            onClose={onCloseHandler}
                                        />
                                    </div>
                                </TabsContent>
                                <TabsContent value="2">
                                    <div className="flex flex-col gap-3">
                                        <Step2 
                                            defaultData={data[2].data}
                                            onNext={(data) => onNextHandler({currentStep: 2, nextStep: 3, data})}
                                            onBack={() => onBackHandler(1)}
                                            onClose={onCloseHandler}
                                        />
                                    </div>
                                </TabsContent>
                                <TabsContent value="3">
                                    <CrmComponentResolver 
                                        selectedCrmSystem={data[2].data.system as crmSystem}
                                        components={{
                                            'airtable': (
                                                <Step3Airtable
                                                    defaultData={data[3].data}
                                                    onNext={(data) => onNextHandler({currentStep: 3, nextStep: 4, data})}
                                                    onBack={() => onBackHandler(2)}
                                                    onClose={onCloseHandler}
                                                />
                                            ),
                                            'salesforce': (
                                                <Step3Salesforce 
                                                    defaultData={data[3].data}
                                                    onNext={(data) => onNextHandler({currentStep: 3, nextStep: 4, data})}
                                                    onBack={() => onBackHandler(2)}
                                                    onClose={onCloseHandler}
                                                />
                                            ),
                                            'collaborate': (
                                                <Step3Salesforce 
                                                    defaultData={data[3].data}
                                                    onNext={(data) => onNextHandler({currentStep: 3, nextStep: 4, data})}
                                                    onBack={() => onBackHandler(2)}
                                                    onClose={onCloseHandler}
                                                />
                                            ),
                                        }}
                                    />
                                </TabsContent>
                                <TabsContent value="4">
                                    <CrmComponentResolver 
                                        selectedCrmSystem={data[2].data.system as crmSystem}
                                        components={{
                                            'airtable': (
                                                <Step4Airtable
                                                    data={data}
                                                    onBack={() => onBackHandler(3)}
                                                    onClose={onCloseHandler}
                                                />
                                            ),
                                            'salesforce': (
                                                <Step4Salesforce 
                                                    data={data}
                                                    onBack={() => onBackHandler(3)}
                                                    onClose={onCloseHandler}
                                                />
                                            ),
                                            'collaborate': (
                                                <Step4Salesforce 
                                                    data={data}
                                                    onBack={() => onBackHandler(3)}
                                                    onClose={onCloseHandler}
                                                />
                                            ),
                                        }}
                                    />
                                </TabsContent>
                            </Tabs>
                        </div>
                    </ScrollArea>
                </DialogContent>
            </Dialog>
        </>

    )
}

export { AddCrmModal as AddCrmProjectModal };

